'use client';

import * as React from 'react';
import { usePathname } from 'next/navigation';

/**
 * Detects password-recovery hash fragments in the URL and redirects to
 * /reset-password with the hash preserved. The GoTrueClient inside
 * createBrowserClient auto-processes hash fragments when first constructed,
 * so a full page navigation to /reset-password#... ensures a fresh client
 * picks up the tokens and establishes a session before the form renders.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const didHandleRef = React.useRef(false);

  React.useEffect(() => {
    // Already handled — avoid re-processing on re-renders
    if (didHandleRef.current) return;

    // Already on the reset-password page — let the GoTrueClient on that
    // page handle the hash naturally
    if (pathname === '/reset-password') return;

    const hash = window.location.hash;
    if (!hash.includes('type=recovery')) return;

    didHandleRef.current = true;

    // Navigate to /reset-password with the recovery hash intact.
    // Using window.location.replace causes a full page load, which gives
    // the GoTrueClient a clean initialization and guarantees it detects
    // the hash fragment in its constructor's _recoverAndRefresh() call.
    window.location.replace(`/reset-password${hash}`);
  }, [pathname]);

  return <>{children}</>;
}
